import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { LocationPicker, type LocationData } from "./LocationPicker";
import { TimePicker } from "./TimePicker";
import { HabitFormData } from "./AddHabitDialog";

export interface EditHabitData {
  id: string;
  name: string;
  frequencyType: string;
  frequencyCount: number;
  evidenceType: string | null;
  isPrivate: boolean;
  locationLat: number | null;
  locationLng: number | null;
  locationRadius: number | null;
  scheduledTime: string | null;
  timezone: string | null;
}

interface EditHabitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  habit: EditHabitData | null;
  onSubmit: (habitId: string, data: HabitFormData) => Promise<void>;
}

export const EditHabitDialog = ({ open, onOpenChange, habit, onSubmit }: EditHabitDialogProps) => {
  const [name, setName] = useState("");
  const [frequencyType, setFrequencyType] = useState<HabitFormData["frequencyType"]>("daily");
  const [frequencyCount, setFrequencyCount] = useState(1);
  const [evidenceType, setEvidenceType] = useState<HabitFormData["evidenceType"]>("none");
  const [isPrivate, setIsPrivate] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [scheduledTime, setScheduledTime] = useState<string | null>(null);

  // Populate form when habit changes
  useEffect(() => {
    if (habit) {
      setName(habit.name);
      setFrequencyType((habit.frequencyType as HabitFormData["frequencyType"]) || "daily");
      setFrequencyCount(habit.frequencyCount || 1);
      setEvidenceType((habit.evidenceType as HabitFormData["evidenceType"]) || "none");
      setIsPrivate(habit.isPrivate ?? true);
      setScheduledTime(habit.scheduledTime ?? null);
      if (habit.locationLat && habit.locationLng) {
        setLocationData({
          lat: habit.locationLat,
          lng: habit.locationLng,
          radius: habit.locationRadius ?? 200,
        });
      } else {
        setLocationData(null);
      }
    }
  }, [habit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!habit || !name.trim()) return;
    if (evidenceType === "location" && !locationData) return;
    if (evidenceType === "time" && !scheduledTime) return;

    setIsLoading(true);
    try {
      await onSubmit(habit.id, {
        name: name.trim(),
        frequencyType,
        frequencyCount,
        evidenceType,
        isPrivate,
        locationData: evidenceType === "location" ? locationData : null,
        scheduledTime: evidenceType === "time" ? scheduledTime : null,
        timezone: evidenceType === "time" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
      });
      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  const frequencyLabels = {
    daily: "dia(s)",
    weekly: "semana(s)",
    monthly: "mês(es)",
    yearly: "ano(s)",
  };

  if (!habit) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border max-h-[90vh] overflow-y-auto p-6 sm:p-6">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-2xl font-display text-center flex items-center justify-center gap-2">
            <span>✏️</span> Editar Hábito
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="edit-name" className="font-semibold">
              Nome do hábito
            </Label>
            <Input
              id="edit-name"
              placeholder="Ex: Meditar, Exercitar, Ler..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
            />
          </div>

          {/* Frequency Type */}
          <div className="space-y-3">
            <Label className="font-semibold">Frequência</Label>
            <RadioGroup
              value={frequencyType}
              onValueChange={(v) => setFrequencyType(v as HabitFormData["frequencyType"])}
              className="grid grid-cols-2 gap-2"
            >
              {(["daily", "weekly", "monthly", "yearly"] as const).map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <RadioGroupItem value={type} id={`edit-${type}`} />
                  <Label htmlFor={`edit-${type}`} className="cursor-pointer capitalize">
                    {type === "daily" && "Diário"}
                    {type === "weekly" && "Semanal"}
                    {type === "monthly" && "Mensal"}
                    {type === "yearly" && "Anual"}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Frequency Count */}
          <div className="space-y-2">
            <Label htmlFor="edit-count" className="font-semibold">
              Quantas vezes por {frequencyLabels[frequencyType].replace("(s)", "")}?
            </Label>
            <Select
              value={frequencyCount.toString()}
              onValueChange={(v) => setFrequencyCount(parseInt(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <SelectItem key={n} value={n.toString()}>
                    {n} vez{n > 1 ? "es" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Evidence Type */}
          <div className="space-y-3">
            <Label className="font-semibold">Tipo de evidência</Label>
            <RadioGroup
              value={evidenceType}
              onValueChange={(v) => setEvidenceType(v as HabitFormData["evidenceType"])}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="none" id="edit-none" />
                <Label htmlFor="edit-none" className="cursor-pointer">Nenhuma</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="photo" id="edit-photo" />
                <Label htmlFor="edit-photo" className="cursor-pointer">📷 Foto</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="confirmation" id="edit-confirmation" />
                <Label htmlFor="edit-confirmation" className="cursor-pointer">👥 Confirmação de outro usuário</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="location" id="edit-location" />
                <Label htmlFor="edit-location" className="cursor-pointer">📍 Localização</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="time" id="edit-time" />
                <Label htmlFor="edit-time" className="cursor-pointer">⏰ Horário</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Location picker */}
          {evidenceType === "location" && (
            <LocationPicker value={locationData} onChange={setLocationData} />
          )}

          {/* Time picker */}
          {evidenceType === "time" && (
            <TimePicker value={scheduledTime} onChange={setScheduledTime} />
          )}

          {/* Privacy */}
          <div className="flex items-center justify-between">
            <Label htmlFor="edit-private" className="font-semibold">
              Hábito privado
            </Label>
            <Switch
              id="edit-private"
              checked={isPrivate}
              onCheckedChange={setIsPrivate}
            />
          </div>

          <Button
            type="submit"
            variant="coral"
            size="lg"
            className="w-full mt-6 mb-2"
            disabled={isLoading || !name.trim() || (evidenceType === "location" && !locationData) || (evidenceType === "time" && !scheduledTime)}
          >
            {isLoading ? (
              <Loader2 className="animate-spin" />
            ) : (
              <>Salvar alterações ✏️</>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
